import { CSSProperties, FC, Fragment, memo, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import classNames from 'classnames'
import gsap from 'gsap'

import css from './TeamModal.module.scss'

import { ModalColumnsSetupType, PageCopyType } from '@/data/types'

import FocusService from '@/services/focus'
import LockBodyScrollService from '@/services/lock-body-scroll'

import { useRefs } from '@/hooks/use-refs'

import Label from '@/components/Label/Label'
import Paragraph from '@/components/Paragraph/Paragraph'
import PillButton from '@/components/PillButton/PillButton'

import CloseButtonSvg from '@/svgs/close.svg'

export type TeamModalProps = {
  className?: string
  content: PageCopyType['about']['team']
  columns: ModalColumnsSetupType
}

export type ViewRefs = {
  root: HTMLDivElement
  contentWrapper: HTMLDivElement
  content: HTMLDivElement
  background: HTMLButtonElement
  closePill: HTMLElement
}

const TeamModal: FC<TeamModalProps> = ({ className, content, columns }) => {
  const [activeModal, setActiveModal] = useState(false)
  const [animateModal, setAnimateModal] = useState(false)
  const viewRefs = useRefs<ViewRefs>()

  useEffect(() => {
    if (activeModal) {
      LockBodyScrollService.lock()
      FocusService.setContainer(viewRefs.root.current)
    } else {
      FocusService.reset()
      LockBodyScrollService.unlock()
    }
  }, [activeModal, viewRefs])

  useEffect(() => {
    if (!activeModal) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveModal(false)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeModal])

  useEffect(() => {
    return () => {
      FocusService.reset()
      LockBodyScrollService.unlock()
    }
  }, [])

  useEffect(() => {
    if (!viewRefs.root.current) {
      return
    }

    if (activeModal) {
      const closeSvg = viewRefs.root.current.querySelector(`.${css.svg}`)
      const allSections = Array.from(viewRefs.content.current!.querySelectorAll(`.${css.section}`))

      // Record padding and width of close pill
      const closePillPadding = gsap.getProperty(viewRefs.closePill.current!, 'padding')
      const closePillWidth = gsap.getProperty(viewRefs.closePill.current!, 'width')

      gsap
        .timeline({
          onStart: () => {
            setAnimateModal(true)
          }
        })
        .set(viewRefs.root.current, { opacity: 1 }, 0)
        .fromTo(
          viewRefs.background.current!,
          { opacity: 0 },
          { opacity: 1, duration: 0.8, ease: 'ease-opacity' },
          '<+0.1'
        )
        .maskWipeIn(
          viewRefs.contentWrapper.current!,
          { direction: 'left', duration: 0.8, ease: 'ease-wipe', immediateRender: true },
          '<+0.1'
        )
        .fromTo(
          viewRefs.closePill.current!,
          { opacity: 0, width: 0, padding: 0 },
          {
            opacity: 1,
            width: closePillWidth,
            padding: closePillPadding,
            duration: 0.45,
            ease: 'ease-transform'
          },
          '<'
        )
        .fromTo(closeSvg, { opacity: 0 }, { opacity: 1, duration: 0.334, ease: 'ease-opacity' }, '<+0.2')
        .fromTo(closeSvg, { rotate: 90 }, { rotate: 0, duration: 0.667, ease: 'ease-transform' }, '<')

        .fromTo(allSections, { y: 25 }, { y: 0, duration: 1, ease: 'ease-transform', stagger: 0.08 }, '<')
        .fromTo(allSections, { opacity: 0 }, { opacity: 1, duration: 1, stagger: 0.08, ease: 'ease-opacity' }, '<')
    } else {
      gsap
        .timeline({
          onComplete: () => {
            setAnimateModal(false)
          }
        })
        .to(viewRefs.root.current, { opacity: 0, duration: 0.667, ease: 'ease-opacity' }, 0)
    }
  }, [activeModal, viewRefs])

  const modalContent =
    activeModal || animateModal ? (
      <div className={classNames('ModalContent', css.modalWrapper)} ref={viewRefs.root}>
        <button
          aria-label="Close team credits"
          className={css.background}
          onClick={() => setActiveModal(false)}
          ref={viewRefs.background}
          type="button"
        />
        <div
          className={css.modal}
          style={
            {
              '--columns-mobile': columns.mobile,
              '--columns-tablet': columns.tablet,
              '--columns-desktop': columns.desktop
            } as CSSProperties
          }
        >
          <PillButton
            className={css.pillButton}
            theme="modal"
            size="close"
            onClick={() => setActiveModal(false)}
            ref={viewRefs.closePill}
          >
            <CloseButtonSvg className={css.svg} />
          </PillButton>

          <div className={css.wrapper} ref={viewRefs.contentWrapper}>
            <div className={css.scroller}>
              <div className={css.content} ref={viewRefs.content}>
                {content?.map((item, index) => (
                  <div className={css.section} key={index}>
                    <Label className={css.label} labelType={3}>
                      {item.label}
                    </Label>
                    <ul>
                      {item.members.map((member, memberIndex) => {
                        // if member is a string
                        if (typeof member === 'string') {
                          return (
                            <Paragraph
                              className={css.member}
                              as="li"
                              uppercase={false}
                              italic
                              paragraphType={2}
                              key={`${index}-${memberIndex}`}
                            >
                              {member}
                            </Paragraph>
                          )
                        } else if (typeof member === 'object') {
                          return (
                            <Fragment key={`${index}-${memberIndex}`}>
                              <Label className={classNames(css.label, css.nestedLabel)} labelType={3}>
                                {member.label}
                              </Label>
                              <ul>
                                {member.members.map((member, nestedIndex) => (
                                  <Paragraph
                                    className={css.member}
                                    as="li"
                                    uppercase={false}
                                    italic
                                    paragraphType={2}
                                    key={`${index}-${memberIndex}-${nestedIndex}`}
                                  >
                                    {member}
                                  </Paragraph>
                                ))}
                              </ul>
                            </Fragment>
                          )
                        }
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : null

  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setModalRoot(document.getElementById('modal-root'))
  }, [])

  return (
    <div className={classNames('TeamModal', css.root, className)}>
      <button className={css.clickMe} onClick={() => setActiveModal(!activeModal)}>
        Team Credits
      </button>

      {modalRoot ? ReactDOM.createPortal(modalContent, modalRoot) : null}
    </div>
  )
}

export default memo(TeamModal)
